import React, { useRef, useState, useCallback } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Eraser, Check, Loader2, AlertTriangle } from 'lucide-react';
import { apiClient, getApiErrorMessage } from '../../api/client';
import { putToPresignedUrl } from '../../api/upload';

export interface SignatureCaptureProps {
  formName: string;
  submissionId: string;
  onSigned: (r2Key: string) => void;
  onCleared?: () => void;
  label?: string;
  /**
   * The `uploads.entity_type` the signature files under. Defaults to 'safety',
   * which is what every caller before the inspection forms wanted, so they are
   * untouched by this prop existing.
   *
   * INSPECTIONS MUST PASS 'inspection_signature', NOT 'inspections'. The photo
   * queries behind InspectionDetailPage and the inspection PDF are both
   * `WHERE entity_type = 'inspections' AND entity_id = $2` with no further
   * filter, so a signature filed under that type renders as a tile inside the
   * Photos grid — on the page and in the printed record, next to the signature
   * block it is a duplicate of.
   *
   * The value is an allow-list on the API (`uploads.routes.ts`); an unlisted
   * one 400s rather than filing somewhere unexpected.
   */
  entityType?: string;
}

export default function SignatureCapture({
  formName,
  submissionId,
  onSigned,
  onCleared,
  label = 'Sign here',
  entityType = 'safety',
}: SignatureCaptureProps) {
  const sigRef = useRef<SignatureCanvas | null>(null);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [savedUrl, setSavedUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleClear = useCallback(() => {
    sigRef.current?.clear();
    setHasDrawn(false);
    setSavedKey(null);
    setSavedUrl(null);
    setError('');
    onCleared?.();
  }, [onCleared]);

  const handleConfirm = useCallback(async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) return;

    setUploading(true);
    setError('');

    try {
      const canvas = sigRef.current.getCanvas();
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Canvas export failed'))), 'image/png');
      });

      const fileName = `signature-${Date.now()}.png`;

      // Step 1: get presigned URL
      const { data: presigned } = await apiClient.post('/uploads/presigned-url', {
        fileName,
        contentType: 'image/png',
        entityType,
        entityId: submissionId,
      });

      // Step 2: upload to R2. Throws unless R2 answered 2xx — so steps 3 and 4
      // below are unreachable for a signature that is not in the bucket.
      await putToPresignedUrl(presigned.uploadUrl, blob, 'image/png');

      // Step 3: confirm
      await apiClient.post('/uploads/confirm', {
        key: presigned.key,
        size: blob.size,
      });

      // Step 4: and only now does the preview appear.
      //
      // This ordering is the fix. `savedUrl` is the canvas' own toDataURL, so
      // it renders a perfect picture of the signature whether or not anything
      // reached storage — it was the most convincing part of the lie. It is
      // still the source of the pixels (re-fetching a signed URL for an image
      // we already hold is pure latency), but it is now written *after* a
      // verified PUT and a confirmed key, so it can only appear for a
      // signature that exists server-side.
      const dataUrl = canvas.toDataURL('image/png');
      setSavedKey(presigned.key);
      setSavedUrl(dataUrl);
      onSigned(presigned.key);
    } catch (err: any) {
      console.error('Signature upload failed:', err);
      // Left on screen until the user acts. Auto-clearing after 4s put the
      // form back to looking merely unsigned, which reads as "I haven't
      // pressed Confirm yet" rather than "that did not save".
      setError(getApiErrorMessage(err, 'Signature did not save. Try again.'));
    } finally {
      setUploading(false);
    }
    // `formName` is deliberately absent: the callback does not read it. It was
    // in this array before `entityType` joined it, which made the rule flag the
    // whole list rather than the one stale entry.
  }, [submissionId, entityType, onSigned]);

  return (
    <div className="bg-fieldiq-card rounded-xl p-4">
      <h3 className="font-semibold mb-3 text-white">{label}</h3>

      {savedUrl ? (
        <div>
          <div className="bg-white rounded-lg p-2 mb-3">
            <img src={savedUrl} alt="Signature" className="max-h-[120px] mx-auto" />
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="flex items-center gap-2 text-sm text-fieldiq-text hover:text-white transition-colors"
          >
            <Eraser size={16} /> Re-sign
          </button>
        </div>
      ) : (
        <div>
          <div
            className="bg-white rounded-lg overflow-hidden border-2 border-fieldiq-border mb-3 relative"
            style={{ touchAction: 'none' }}
          >
            <SignatureCanvas
              ref={sigRef}
              penColor="#1a202c"
              canvasProps={{
                className: 'w-full',
                style: { width: '100%', height: 150, minWidth: 300 },
              }}
              onEnd={() => setHasDrawn(true)}
            />
            {!hasDrawn && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-gray-400 text-sm">{label}</span>
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 text-xs text-red-300 bg-red-900/30 border border-red-500/40 rounded-lg px-3 py-2 mb-2">
              <AlertTriangle size={14} className="shrink-0 mt-px" />
              <span>
                <strong className="font-semibold">Not saved.</strong> {error}
              </span>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClear}
              className="flex items-center gap-1.5 px-3 py-2 bg-fieldiq-border text-fieldiq-text rounded-lg text-sm hover:text-white transition-colors"
            >
              <Eraser size={14} /> Clear
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!hasDrawn || uploading}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                hasDrawn && !uploading
                  ? 'bg-fieldiq-success text-white hover:bg-fieldiq-success/90'
                  : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
              }`}
            >
              {uploading ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Uploading...
                </>
              ) : (
                <>
                  <Check size={14} /> Confirm Signature
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
